/* ═══════════════════════════════════════════════════════════════
   Pocket Tracker Landing Page — Scripts
   ═══════════════════════════════════════════════════════════════ */

// ─── Forward invite code to /app links ───────────────────────────
const _inviteParam = new URLSearchParams(window.location.search).get("invite");
if (_inviteParam) {
  document.querySelectorAll('a[href^="/app"]').forEach((a) => {
    const url = new URL(a.href, window.location.origin);
    url.searchParams.set("invite", _inviteParam);
    a.href = url.pathname + "?" + url.searchParams.toString() + url.hash;
  });
}

// ─── Scroll fade-in via IntersectionObserver ──────────────────────
const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add("visible");
        observer.unobserve(e.target);
      }
    });
  },
  { threshold: 0.15 }
);
document.querySelectorAll(".fade-in").forEach((el) => observer.observe(el));

// ─── Smooth scroll for anchor links ──────────────────────────────
document.querySelectorAll('a[href^="#"]').forEach((a) => {
  a.addEventListener("click", (e) => {
    const target = document.querySelector(a.getAttribute("href"));
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
    }
  });
});

// ─── Mobile hamburger toggle ─────────────────────────────────────
const navToggle = document.getElementById("nav-toggle");
const navMenu = document.getElementById("nav-menu");
if (navToggle && navMenu) {
  navToggle.addEventListener("click", () => {
    navToggle.classList.toggle("open");
    navMenu.classList.toggle("open");
  });

  // Close menu when a link is clicked
  navMenu.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      navToggle.classList.remove("open");
      navMenu.classList.remove("open");
    });
  });
}

// ─── Hero screenshot scroll-driven scale ─────────────────────────
const heroImg = document.querySelector(".hero-img");
const heroMedia = document.querySelector(".hero-media");
if (heroImg && heroMedia) {
  const MIN_SCALE = 0.85;
  const MAX_SCALE = 1;
  let maxReached = MIN_SCALE;
  function updateHeroScale() {
    const rect = heroMedia.getBoundingClientRect();
    const viewH = window.innerHeight;
    const progress = Math.min(Math.max((viewH - rect.top) / viewH, 0), 1);
    const scale = MIN_SCALE + (MAX_SCALE - MIN_SCALE) * progress;
    maxReached = Math.max(maxReached, scale);
    heroImg.style.setProperty("--hero-scale", maxReached);
  }
  window.addEventListener("scroll", updateHeroScale, { passive: true });
  updateHeroScale();
}

// ─── Auth-aware CTA: show appropriate buttons ───────────────────
var _isLoggedIn = false;

function applyLoggedInButtons(lang) {
  lang = lang || localStorage.getItem("ui-lang") || "en";
  const label = translations[lang]["nav.app"] || "Go to App";
  document.querySelectorAll(".cta-login").forEach((btn) => {
    btn.classList.remove("hidden", "btn-secondary");
    btn.classList.add("btn-go-app");
    btn.textContent = label;
    btn.href = "/app";
    btn.removeAttribute("data-i18n"); // prevent setLanguage from overwriting
  });
  document.querySelectorAll(".cta-register").forEach((btn) => {
    btn.classList.add("hidden");
  });
  document.querySelectorAll(".cta-go-app").forEach((btn) => {
    btn.textContent = label;
    btn.classList.remove("hidden");
  });
  // "Already have an account? Sign in" makes no sense once signed in, and its
  // link was just turned into a second "Go to App" button above.
  document.querySelectorAll(".login-link").forEach((p) => p.classList.add("hidden"));
}

fetch("/api/auth/me", { credentials: "same-origin" })
  .then((r) => r.json())
  .then((data) => {
    if (data && data.id) {
      _isLoggedIn = true;
      applyLoggedInButtons();
    } else {
      document.querySelectorAll(".cta-login").forEach((btn) => {
        btn.classList.remove("hidden");
      });
    }
  })
  .catch(() => {
    document.querySelectorAll(".cta-login").forEach((btn) => {
      btn.classList.remove("hidden");
    });
  });


// ─── i18n Translations ───────────────────────────────────────────
const translations = {
  en: {
    'nav.about': 'About',
    'nav.guide': 'Guide',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.signin': 'Sign in',
    'nav.register': 'Register',
    'nav.app': 'Go to App',
    'hero.title1': 'Always Stay',
    'hero.title2': 'One Step Ahead.',
    'hero.subtitle': 'The free companion app for Pokémon TCG Pocket \u2014 record your games in seconds, track your stats and ranked climb, study the meta, and play full games right in your browser.',
    'hero.subtitle.mobile': 'The free companion app for Pokémon TCG Pocket \u2014 record your games in seconds, track your stats and ranked climb, study the meta, and play full games right in the app.',
    'hero.cta1': 'Start Playing &mdash; It\'s Free',
    'hero.cta2': 'See Features',
    'store.tagline': 'Also available on mobile:',
    'store.tagline.mobile': 'Get the free app:',
    'store.browserLink': 'or play in your browser',
    'features.title': 'Everything You Need to Win',
    'features.f4.title': 'Fast Match Tracking',
    'features.f4.desc': 'Log a match in just a few taps &mdash; pick your deck, tap the opponent\'s archetype, mark the result. Add notes, watch your winrates evolve, and filter by deck, matchup or season to see what\'s working.',
    'features.f3.title': 'Deck Management',
    'features.f3.desc': 'Import your decks in standard format, track win/loss stats per deck, view matchup breakdowns, and browse your full match history &mdash; all in one place.',
    'features.f7.title': 'Ranked Tracker',
    'features.f7.desc': 'Follow your ranked season points and win-rate trends on a clean progression chart. Monitor your climb, spot trends, and know exactly where you stand.',
    'features.f9.title': 'Meta Dashboard',
    'features.f9.desc': 'Explore the metagame \u2014 both the tournament meta and the live ranked meta. Archetype usage, deck variants, and a full archetype-vs-archetype Matchup Chart, all from real data.',
    'features.f10.title': 'Battle',
    'features.f10.desc': 'Play full games right inside Pocket Tracker with a complete rules engine. Face a friend with a private code or get matched with another player, skip the animations for fast games, rewatch the replay of any match, and switch to analysis mode to test other lines of play.',
    'features.f6.title': 'Leaderboard',
    'features.f6.desc': 'Browse the top tournament players worldwide, plus live ranked-season leaderboards. Search any player and explore their history, placements and decklists.',
    'features.f1.title': 'Deck Prediction',
    'features.f1.desc': 'Optional power-user mode: get a live read on your opponent\'s likely deck and hand, based on thousands of real tournament decklists.',
    'features.newBadge': 'NEW',
    'features.f11.title': 'Tournament Companion',
    'features.f11.desc': 'Follow your live Limitless tournaments in real time: your next opponent and their decklist, your table, standings, bracket and the field &mdash; plus a cut advisor that tells you exactly what you need to make the cut. Browse your full tournament history and per-season stats too.',
    'features.f8.title': '100% Free',
    'features.f8.desc': 'No cost, no paywall, no data selling. Just sign up and start playing.',
    'features.f8.desc.mobile': 'No cost, no paywall, no data selling. Just download the app and start playing.',
    'cta.title': 'Ready to Gain the Edge?',
    'cta.subtitle': 'Join players already using Pocket Tracker to improve their game.',
    'cta.subtitle.mobile': 'Download the free app and join the players already using Pocket Tracker to improve their game.',
    'cta.btn': 'Create Free Account',
    'cta.login': 'Already have an account?',
    'footer.about': 'About',
    'footer.guide': 'Guide',
    'footer.faq': 'FAQ',
    'footer.contact': 'Contact',
    'footer.terms': 'Terms of Use',
    'footer.privacy': 'Privacy Policy',
    'footer.cookies': 'Cookie Policy',
    'footer.legal': 'Legal Notice',
    'footer.cookieSettings': 'Cookie Settings',
    'footer.disclaimer1': 'Built with the help of AI.',
    'footer.disclaimer2': 'Pokémon, Pokémon TCG, and Pokémon TCG Pocket are copyrights and trademarks of Nintendo, Creatures Inc., GAME FREAK inc., DeNA Co., Ltd., and The Pokémon Company. Pocket Tracker is an unofficial fan project and is not affiliated with, endorsed by, sponsored by, or associated with any of these companies.'
  },
  fr: {
    'nav.about': 'À propos',
    'nav.guide': 'Guide',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.signin': 'Se connecter',
    'nav.register': 'S\'inscrire',
    'nav.app': 'Accéder à l\'app',
    'hero.title1': 'Gardez toujours',
    'hero.title2': 'Une longueur d\'avance.',
    'hero.subtitle': 'L\'app compagnon gratuite pour Pokémon TCG Pocket — enregistrez vos parties en quelques secondes, suivez vos stats et votre montée en classé, étudiez la méta, et jouez de vraies parties directement dans votre navigateur.',
    'hero.subtitle.mobile': 'L\'app compagnon gratuite pour Pokémon TCG Pocket — enregistrez vos parties en quelques secondes, suivez vos stats et votre montée en classé, étudiez la méta, et jouez de vraies parties directement dans l\'app.',
    'hero.cta1': 'Commencer à jouer &mdash; C\'est gratuit',
    'hero.cta2': 'Voir les fonctionnalités',
    'store.tagline': 'Disponible aussi sur mobile :',
    'store.tagline.mobile': 'Téléchargez l\'app gratuite :',
    'store.browserLink': 'ou jouez dans votre navigateur',
    'features.title': 'Tout pour gagner',
    'features.f4.title': 'Suivi des matchs rapide',
    'features.f4.desc': 'Enregistrez une partie en quelques touches &mdash; choisissez votre deck, touchez l\'archétype de l\'adversaire, indiquez le résultat. Ajoutez des notes, observez l\'évolution de votre winrate, et filtrez par deck, matchup ou saison pour voir ce qui fonctionne.',
    'features.f3.title': 'Gestion des decks',
    'features.f3.desc': 'Importez vos decks, suivez vos statistiques de victoire/défaite, analysez vos matchups et parcourez l\'historique de vos matchs &mdash; tout au même endroit.',
    'features.f7.title': 'Suivi du classé',
    'features.f7.desc': 'Suivez vos points et tendances de winrate en saison classée sur un graphique de progression clair. Visualisez votre ascension et identifiez vos tendances.',
    'features.f9.title': 'Tableau de bord Méta',
    'features.f9.desc': 'Explorez le m\u00e9tagame &mdash; la m\u00e9ta de tournoi comme la m\u00e9ta class\u00e9e en direct. Utilisation des arch\u00e9types, variantes de decks, et un Tableau des matchups complet arch\u00e9type contre arch\u00e9type, le tout \u00e0 partir de donn\u00e9es r\u00e9elles.',
    'features.f10.title': 'Combat',
    'features.f10.desc': 'Jouez de vraies parties directement dans Pocket Tracker gr\u00e2ce \u00e0 un moteur de r\u00e8gles complet. Affrontez un ami avec un code priv\u00e9 ou soyez mis en relation avec un autre joueur, passez les animations pour des parties rapides, revoyez le replay de chaque match, et basculez en mode analyse pour tester d\'autres lignes de jeu.',
    'features.f6.title': 'Classement',
    'features.f6.desc': 'Parcourez le top des joueurs de tournoi mondiaux, ainsi que les classements de saison class\u00e9e en direct. Recherchez un joueur et explorez son historique, ses classements et ses decklists.',
    'features.f1.title': 'Pr\u00e9diction de deck',
    'features.f1.desc': 'Mode expert optionnel : obtenez une estimation en direct du deck et de la main probables de votre adversaire, bas\u00e9e sur des milliers de decklists de tournois r\u00e9els.',
    'features.newBadge': 'NOUVEAU',
    'features.f11.title': 'Compagnon de tournoi',
    'features.f11.desc': 'Suivez vos tournois Limitless en direct : votre prochain adversaire et sa decklist, votre table, le classement, l\'arbre et les participants \u2014 avec un conseiller de cut qui vous dit exactement ce qu\'il vous faut pour passer le cut. Consultez aussi tout votre historique de tournois et vos statistiques par saison.',
    'features.f8.title': '100% gratuit',
    'features.f8.desc': 'Aucun coût, aucun paywall, aucune revente de données. Inscrivez-vous et commencez à jouer.',
    'features.f8.desc.mobile': 'Aucun coût, aucun paywall, aucune revente de données. Téléchargez l\'app et commencez à jouer.',
    'cta.title': 'Prêt à prendre l\'avantage ?',
    'cta.subtitle': 'Rejoignez les joueurs qui utilisent déjà Pocket Tracker pour s\'améliorer.',
    'cta.subtitle.mobile': 'Téléchargez l\'app gratuite et rejoignez les joueurs qui utilisent déjà Pocket Tracker pour s\'améliorer.',
    'cta.btn': 'Créer un compte gratuit',
    'cta.login': 'Déjà un compte ?',
    'footer.about': 'À propos',
    'footer.guide': 'Guide',
    'footer.faq': 'FAQ',
    'footer.contact': 'Contact',
    'footer.terms': 'Conditions d\'utilisation',
    'footer.privacy': 'Politique de confidentialité',
    'footer.cookies': 'Politique des cookies',
    'footer.legal': 'Mentions légales',
    'footer.cookieSettings': 'Paramètres des cookies',
    'footer.disclaimer1': 'Conçu avec l\'aide de l\'IA.',
    'footer.disclaimer2': 'Pokémon, Pokémon TCG et Pokémon TCG Pocket sont des droits d\'auteur et des marques déposées de Nintendo, Creatures Inc., GAME FREAK inc., DeNA Co., Ltd. et The Pokémon Company. Pocket Tracker est un projet non officiel et n\'est en aucun cas affilié, soutenu ou sponsorisé par ces sociétés.'
  },
  ja: {
    'nav.about': 'アプリについて',
    'nav.guide': 'ガイド',
    'nav.faq': 'よくある質問',
    'nav.contact': 'お問い合わせ',
    'nav.signin': 'ログイン',
    'nav.register': '新規登録',
    'nav.app': 'アプリを開く',
    'hero.title1': '常に、',
    'hero.title2': '一歩先へ。',
    'hero.subtitle': 'ポケポケ（Pokémon TCG Pocket）のための無料コンパニオンアプリ — 対戦を数秒で記録し、成績とランクの推移を追跡し、環境を研究して、ブラウザでそのまま本格的な対戦をプレイできます。',
    'hero.subtitle.mobile': 'ポケポケ（Pokémon TCG Pocket）のための無料コンパニオンアプリ — 対戦を数秒で記録し、成績とランクの推移を追跡し、環境を研究して、アプリでそのまま本格的な対戦をプレイできます。',
    'hero.cta1': '無料で始める',
    'hero.cta2': '機能を見る',
    'store.tagline': 'モバイルでも利用できます：',
    'store.tagline.mobile': '無料アプリを入手：',
    'store.browserLink': 'またはブラウザでプレイ',
    'features.title': '勝つために必要なすべてを',
    'features.f4.title': 'すばやい対戦記録',
    'features.f4.desc': '対戦は数タップで記録完了 &mdash; 自分のデッキを選び、相手のアーキタイプをタップして、結果をマークするだけ。メモを追加し、勝率の推移を確認し、デッキ・相性・シーズンで絞り込んで、何がうまくいっているかを把握できます。',
    'features.f3.title': 'デッキ管理',
    'features.f3.desc': '標準形式でデッキをインポートし、デッキごとの勝敗を記録。相性の内訳を確認し、対戦履歴をひとつの場所でまとめて閲覧できます。',
    'features.f7.title': 'ランク記録',
    'features.f7.desc': 'ランクマッチのシーズンポイントと勝率の推移を、見やすいグラフで追跡。連勝も不調もひと目でわかり、自分の現在地がはっきりします。',
    'features.f9.title': '環境ダッシュボード',
    'features.f9.desc': '大会環境もランクマッチ環境も探索できます。アーキタイプ使用率、デッキバリアント、そしてアーキタイプ同士の完全な相性表 — すべて実データに基づいています。',
    'features.f10.title': 'バトル',
    'features.f10.desc': '完全なルールエンジンで、Pocket Trackerの中でそのまま本格的な対戦をプレイ。プライベートコードでフレンドと対戦するかマッチングで相手を見つけ、アニメーションをスキップして高速対戦、対戦後はリプレイを見返し、分析モードで別のプレイラインを検証できます。',
    'features.f6.title': 'リーダーボード',
    'features.f6.desc': '世界中の大会トッププレイヤーと、ランクマッチシーズンのライブ順位表を閲覧。プレイヤーを検索して、履歴・順位・デッキレシピを確認できます。',
    'features.f1.title': 'デッキ予測',
    'features.f1.desc': '任意の上級者向けモード。数千件の実際の大会デッキレシピに基づいて、相手のデッキと手札をリアルタイムで推定します。',
    'features.newBadge': 'NEW',
    'features.f11.title': 'トーナメントコンパニオン',
    'features.f11.desc': 'Limitlessのトーナメントをリアルタイムで追跡。次の対戦相手とそのデッキレシピ、テーブル、順位表、ブラケット、環境を確認でき、トップカット進出に何が必要かを教えてくれるカット・アドバイザーも搭載。過去のトーナメント履歴やシーズンごとの成績も閲覧できます。',
    'features.f8.title': '100%無料',
    'features.f8.desc': '料金なし、ペイウォールなし、データ販売なし。登録するだけで始められます。',
    'features.f8.desc.mobile': '料金なし、ペイウォールなし、データ販売なし。アプリをダウンロードするだけで始められます。',
    'cta.title': '一歩先へ行く準備はできましたか？',
    'cta.subtitle': 'Pocket Trackerで上達を目指すプレイヤーたちに加わりましょう。',
    'cta.subtitle.mobile': '無料アプリをダウンロードして、Pocket Trackerで上達を目指すプレイヤーたちに加わりましょう。',
    'cta.btn': '無料アカウントを作成',
    'cta.login': 'すでにアカウントをお持ちですか？',
    'footer.about': 'アプリについて',
    'footer.guide': 'ガイド',
    'footer.faq': 'よくある質問',
    'footer.contact': 'お問い合わせ',
    'footer.terms': '利用規約',
    'footer.privacy': 'プライバシーポリシー',
    'footer.cookies': 'Cookieポリシー',
    'footer.legal': '法的表記',
    'footer.cookieSettings': 'Cookie設定',
    'footer.disclaimer1': '本アプリはAIの支援を受けて開発されています。',
    'footer.disclaimer2': 'Pokémon、Pokémon TCG、およびPokémon TCG Pocketは、Nintendo、Creatures Inc.、GAME FREAK inc.、DeNA Co., Ltd.、The Pokémon Companyの著作権および商標です。Pocket Trackerは非公式のファンプロジェクトであり、これらの企業とは提携・承認・後援・関係が一切ありません。'
  }
};

// Languages that have static page mirrors (/fr/<page>, /ja/<page>) and can be
// auto-selected from the browser locale. Must match the routes in server.js.
const SUPPORTED_LANGS = ["en", "fr", "ja"];

const langSelector = document.getElementById("landing-lang-selector");

// iPhone/iPad/Android visitors are pointed at the store app rather than the
// browser (see the platform-* class set in <head>), so strings with a ".mobile"
// variant use it there. The HTML defaults stay the desktop wording.
const IS_MOBILE_DEVICE = /\bplatform-(ios|android)\b/.test(document.documentElement.className);

function setLanguage(lang) {
  if (!translations[lang]) return;
  localStorage.setItem("ui-lang", lang);
  if (langSelector) langSelector.value = lang;
  syncLangPicker(lang);

  // Automatically change lang header
  document.documentElement.lang = lang;
  
  document.querySelectorAll("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    let str = (IS_MOBILE_DEVICE && translations[lang][key + ".mobile"]) || translations[lang][key];
    if (!str && lang !== "en") str = (IS_MOBILE_DEVICE && translations["en"][key + ".mobile"]) || translations["en"][key];
    if (str) el.innerHTML = str;
  });

  // Update content and legal page links to match language
  const langPrefix = lang === "en" ? "" : `/${lang}`;
  document.querySelectorAll('.footer-links a[href*="terms"], .footer-links a[href*="privacy"], .footer-links a[href*="cookies"], .footer-links a[href*="legal"], .footer-links a[href*="about"], .footer-links a[href*="guide"], .footer-links a[href*="faq"], .footer-links a[href*="contact"], .nav-content-link').forEach((a) => {
    // Strip any existing locale prefix (fr/ or ja/) first so re-running this
    // is idempotent — otherwise /ja/faq would become /ja/ja/faq.
    const page = a.getAttribute("href").replace(/^\/((fr|ja)\/)?/, "");
    a.href = `${langPrefix}/${page}`;
  });

  // Automatically update hero images (wide 3-device shot + phone-only variant)
  const heroSuffix = lang === "en" ? "" : `-${lang}`;
  const setSrcset = (sel, path) => { const el = document.querySelector(sel); if (el) el.srcset = path; };
  setSrcset(".hero-src-phone-webp", `/img/landing-hero-phone${heroSuffix}.webp`);
  setSrcset(".hero-src-phone-png", `/img/landing-hero-phone${heroSuffix}.png`);
  setSrcset(".hero-src-wide-webp", `/img/landing-hero-3up${heroSuffix}.webp`);
  const heroImgElem = document.querySelector(".hero-img");
  if (heroImgElem) heroImgElem.src = `/img/landing-hero-3up${heroSuffix}.png`;

  // Localized official store badges (Apple & Google both publish localized art)
  const badgeLang = SUPPORTED_LANGS.includes(lang) ? lang : "en";
  document.querySelectorAll(".badge-appstore").forEach((img) => {
    img.src = `/img/badges/appstore-badge-${badgeLang}.svg`;
  });
  document.querySelectorAll(".badge-googleplay").forEach((img) => {
    img.src = `/img/badges/googleplay-badge-${badgeLang}.svg`;
  });

  // Re-apply "Go to App" buttons if logged in (i18n would have overwritten them)
  if (_isLoggedIn) applyLoggedInButtons(lang);
}

// ─── Language picker with flags ──────────────────────────────────
// A native <select> can't show images, and regional-indicator flag emoji render
// as bare letter pairs ("FR FR") on Windows. So the <select> stays in the markup
// (no-JS fallback, and the single source of the change event) and is replaced
// visually by a button + menu using the same self-hosted SVG flags as the
// app's Limitless leaderboard (/flags/<cc>.svg).
const LANG_FLAGS = { en: "gb", fr: "fr", ja: "jp" };
let langPicker = null;

function flagImg(lang) {
  const img = document.createElement("img");
  img.className = "lang-flag";
  img.src = `/flags/${LANG_FLAGS[lang] || "gb"}.svg`;
  img.width = 20;
  img.height = 15;
  img.alt = "";
  return img;
}

function syncLangPicker(lang) {
  if (!langPicker) return;
  const btn = langPicker.querySelector(".lang-picker-btn");
  btn.replaceChildren(flagImg(lang), document.createTextNode(lang.toUpperCase()));
  langPicker.querySelectorAll("[role=option]").forEach((li) => {
    li.setAttribute("aria-selected", String(li.dataset.value === lang));
  });
}

function buildLangPicker(select) {
  const wrap = document.createElement("div");
  wrap.className = "lang-picker";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "lang-picker-btn";
  btn.setAttribute("aria-haspopup", "listbox");
  btn.setAttribute("aria-expanded", "false");
  btn.setAttribute("aria-label", select.getAttribute("aria-label") || "Language");
  const menu = document.createElement("ul");
  menu.className = "lang-picker-menu";
  menu.setAttribute("role", "listbox");
  menu.hidden = true;
  [...select.options].forEach((opt) => {
    const li = document.createElement("li");
    li.setAttribute("role", "option");
    li.tabIndex = -1;
    li.dataset.value = opt.value;
    li.append(flagImg(opt.value), document.createTextNode(opt.value.toUpperCase()));
    menu.appendChild(li);
  });

  const setOpen = (open) => {
    menu.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
    if (open) (menu.querySelector('[aria-selected="true"]') || menu.firstElementChild).focus();
  };
  const choose = (value) => {
    setOpen(false);
    btn.focus();
    if (value === select.value) return;
    select.value = value;
    select.dispatchEvent(new Event("change"));
  };

  btn.addEventListener("click", () => setOpen(menu.hidden));
  menu.addEventListener("click", (e) => {
    const li = e.target.closest("[role=option]");
    if (li) choose(li.dataset.value);
  });
  menu.addEventListener("keydown", (e) => {
    const items = [...menu.children];
    const i = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); items[(i + 1) % items.length].focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (i >= 0) choose(items[i].dataset.value); }
    else if (e.key === "Escape") { setOpen(false); btn.focus(); }
  });
  document.addEventListener("click", (e) => { if (!wrap.contains(e.target)) setOpen(false); });

  wrap.append(btn, menu);
  select.classList.add("lang-select-native");
  select.after(wrap);
  return wrap;
}

if (langSelector) {
  langPicker = buildLangPicker(langSelector);
  langSelector.addEventListener("change", (e) => {
    const lang = e.target.value;
    localStorage.setItem("ui-lang", lang);
    const pathname = window.location.pathname;

    // Localized landing (/fr/, /ja/): the URL decides the language, so switching
    // must move to the matching landing URL or a reload would revert it.
    if (/^\/(fr|ja)\/?$/.test(pathname)) {
      window.location.href = (lang === "en" ? "/" : `/${lang}/`) + window.location.search;
      return;
    }

    // Auto-redirect static pages when language is toggled manually
    if (pathname !== "/" && pathname !== "" && pathname !== "/app" && !pathname.includes(".html")) {
      const page = pathname.replace(/^\/((fr|ja)\/)?/, "");
      if (page) {
        window.location.href = lang === "en" ? `/${page}` : `/${lang}/${page}`;
        return;
      }
    }
    
    setLanguage(lang);
  });
}

// Init language based on current URL to prevent desync on static pages
const currentPath = window.location.pathname;
let initLang;

const pathLang = SUPPORTED_LANGS.find((l) => currentPath.startsWith(`/${l}/`));
if (pathLang) {
  initLang = pathLang;
  localStorage.setItem("ui-lang", initLang);
} else if (currentPath !== "/" && currentPath !== "" && currentPath !== "/app") {
  initLang = "en";
  localStorage.setItem("ui-lang", initLang);
} else {
  const stored = localStorage.getItem("ui-lang");
  const browserLang = SUPPORTED_LANGS.find((l) => navigator.language.startsWith(l));
  initLang = SUPPORTED_LANGS.includes(stored) ? stored : (browserLang || "en");
}

setLanguage(initLang);

// ─── Footer "Cookie Settings": re-open the analytics consent banner ─
document.querySelectorAll(".cookie-settings-link").forEach((a) => {
  a.addEventListener("click", (e) => {
    e.preventDefault();
    if (typeof window.ptOpenConsent === "function") window.ptOpenConsent();
  });
});
